import './styles/index.css'
import { WelcomeScreen } from './components/WelcomeScreen.js'
import { MenuView } from './components/MenuView.js'
import { RestaurantSelector } from './components/RestaurantSelector.js'
import { OrderConfirmationModal } from './components/OrderConfirmationModal.js'
import { OrderSuccessModal } from './components/OrderSuccessModal.js'
import { PaymentService } from './services/PaymentService.js'
import { UnifiedPaymentModal } from './components/payment/UnifiedPaymentModal.js'
import { LanguageSelector } from './components/LanguageSelector.js'
import { StaffCallModal } from './components/StaffCallModal.js'
import { CartNotepad } from './components/cart/CartNotepad.js'
import { TableNumberBar } from './components/TableNumberBar.js'
import { NoRestaurantScreen } from './components/NoRestaurantScreen.js'
import { Router } from './utils/Router.js'
import { ConfigurationManager } from './services/ConfigurationManager.js'
import { CartManager } from './services/CartManager.js'
import { OrderService } from './services/OrderService.js'
import { I18nService } from './services/I18nService.js'
import { restaurantConfig } from './services/RestaurantConfigService.js'
import deviceManager from './services/DeviceManager.js'

import { AppInitializer } from './core/initialization.js'
import { EventHandlers } from './core/event-handlers.js'
import { PaymentHandlers } from './core/payment-handlers.js'
import { NavigationManager } from './core/navigation.js'
import { RestaurantConfigManager } from './core/restaurant-config.js'

class OlivetoApp {
  constructor() {
    this.router = new Router();
    this.configManager = new ConfigurationManager();
    this.restaurantConfigService = restaurantConfig;
    this.restaurantSelector = new RestaurantSelector(this.configManager);
    this.cartManager = new CartManager();
    this.orderService = new OrderService(this.configManager);
    this.i18n = new I18nService();
    this.paymentService = new PaymentService(this.configManager);
    this.deviceManager = deviceManager;

    this.languageSelector = new LanguageSelector(this.i18n);
    this.staffCallModal = new StaffCallModal(this.i18n);
    this.cartNotepad = new CartNotepad(this.i18n);
    this.tableNumberBar = new TableNumberBar();
    this.noRestaurantScreen = new NoRestaurantScreen();

    this.orderConfirmationModal = null;
    this.orderSuccessModal = null;
    this.unifiedPaymentModal = null;
    this.welcomeScreen = null;
    this.menuView = null;
    this.currentOrder = null;

    this.cartIntegrationSetup = false;
    this.lastItemAddTime = 0;
    this.headerWarningShown = false;
    this.paymentNotificationRetries = 0;

    this.initializer = new AppInitializer(this);
    this.eventHandlers = new EventHandlers(this);
    this.paymentHandlers = new PaymentHandlers(this);
    this.navigationManager = new NavigationManager(this);
    this.restaurantConfigManager = new RestaurantConfigManager(this);

    this.init();
  }

  async init() {
    await this.initializer.initialize();
    this.eventHandlers.setupAllEventListeners();
  }

  createWelcomeScreen(config) {
    return new WelcomeScreen(config);
  }

  createMenuView(config, menuData) {
    return new MenuView(config, menuData, this.i18n);
  }

  createOrderConfirmationModal(config) {
    return new OrderConfirmationModal(config, this.paymentService, this.i18n);
  }

  createOrderSuccessModal(config) {
    return new OrderSuccessModal(config);
  }

  createUnifiedPaymentModal(isBeforeOrder) {
    return new UnifiedPaymentModal(this.paymentService, {
      beforeOrder: isBeforeOrder,
      i18n: this.i18n,
      createDraftOrder: async (method) => {
        const cartItems = this.cartManager.getItems();
        const totals = this.cartManager.getTotals();
        const menuData = this.configManager.getMenuData();
        const paymentProvider = method === 'qr_code' ? 'TPAY' : undefined;
        const orderPayload = this.orderService.createOrderPayload(
          cartItems,
          totals,
          menuData,
          { type: 'dine-in', paymentProvider }
        );
        const order = await this.orderService.createOrder(orderPayload);
        const currency = (cartItems[0]?.currency) || (this.configManager.getCurrentConfig()?.currency) || 'PLN';
        const enrichedOrder = { ...order, items: cartItems, total: totals.total, currency };
        this.currentOrder = enrichedOrder;
        this.cartManager.setPendingOrder(order);
        return enrichedOrder;
      }
    });
  }

  navigateToMenu() {
    this.navigationManager.navigateToMenu();
  }

  navigateToWelcome() {
    this.navigationManager.navigateToWelcome();
  }

  setTableNumber(tableNumber) {
    this.navigationManager.setTableNumber(tableNumber);
  }

  handleViewChange(viewName, data) {
    this.navigationManager.handleViewChange(viewName, data);
  }

  refreshCurrentView() {
    this.navigationManager.refreshCurrentView();
  }

  async switchRestaurant(restaurantId) {
    await this.restaurantConfigManager.switchRestaurant(restaurantId);
  }

  async reloadConfiguration() {
    await this.restaurantConfigManager.reloadConfiguration();
  }

  async testAPIConnection() {
    await this.restaurantConfigManager.testAPIConnection();
  }

  async onConfigurationChange(config, restaurantId) {
    await this.restaurantConfigManager.onConfigurationChange(config, restaurantId);
  }

  async handleOrderConfirmation(orderData) {
    await this.paymentHandlers.handleOrderConfirmation(orderData);
  }

  handlePaymentSuccess(paymentData) {
    this.paymentHandlers.handlePaymentSuccess(paymentData);
  }

  handlePaymentRetry(payment) {
    this.paymentHandlers.handlePaymentRetry(payment);
  }

  handlePaymentCancellation(payment) {
    this.paymentHandlers.handlePaymentCancellation(payment);
  }

  handlePaymentResultAction(action, result) {
    this.paymentHandlers.handlePaymentResultAction(action, result);
  }

  showPaymentNotification(message, status = 'required') {
    this.paymentHandlers.showPaymentNotification(message, status);
  }

  hidePaymentNotification() {
    this.paymentHandlers.hidePaymentNotification();
  }

  handlePaymentSessionTimeout(order) {
    this.paymentHandlers.handlePaymentSessionTimeout(order);
  }

  handleStaffNotification(type, color, message, orderId) {
    this.paymentHandlers.handleStaffNotification(type, color, message, orderId);
  }
}

let olivetoApp = null;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    olivetoApp = new OlivetoApp();
  });
} else {
  olivetoApp = new OlivetoApp();
}

window.addEventListener('message', (event) => {
  if (event.data && event.data.type === 'UPDATE_THEME') {
    const { colors } = event.data;
    if (olivetoApp && olivetoApp.configManager && olivetoApp.configManager.themeManager) {
      olivetoApp.configManager.themeManager.loadFromBackend({
        kioskDisplaySettings: {
          colorPalette: colors
        }
      });
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept();
}
